import {stageWithProfile} from './ProjectStageCache';

export type StageProfile = 'OPPORTUNITY' | 'PLANNING' | 'RUNNING' | 'HALTED' | 'DONE';
export type StageLike = {id?: string | null; name?: string | null; profile?: StageProfile | string | null} | null | undefined;
type HasStage = {stage?: StageLike} | null | undefined;

export const stageProfileOf = (project: HasStage): StageProfile | null =>
	(project?.stage?.profile as StageProfile | undefined) ?? null;
const STAGE_PROFILES: ReadonlyArray<string> = ['OPPORTUNITY', 'PLANNING', 'RUNNING', 'HALTED', 'DONE'];

/**
 * The list group a project belongs to. The first stage with a profile (admin order, from the stage cache) is keyed by
 * the profile, so the profile sections keep their behaviour; any later stage with that profile is keyed by its own id
 * and gets a section of its own. A stage without a profile, or a profile the cache doesn't know, falls back to the
 * profile or the id as before.
 */
export const stageGroupKeyOf = (project: HasStage): string | null => {
	const stage = project?.stage;
	if (!stage) {
		return null;
	}
	const profile = stageProfileOf(project);
	if (!profile) {
		return stage.id ?? null;
	}
	const first = stageWithProfile(profile);
	return first && stage.id && first.id !== stage.id ? stage.id : profile;
};

/** Whether a group key (see stageGroupKeyOf) names a stage keyed by its id rather than a profile. */
export const isCustomStageKey = (key: string | null | undefined): boolean => !!key && !STAGE_PROFILES.includes(key);

export const isProjectLocked = (project: HasStage): boolean => ['DONE', 'HALTED'].includes(stageProfileOf(project) ?? '');
export const isProjectDone = (project: HasStage): boolean => stageProfileOf(project) === 'DONE';
export const isProjectHalted = (project: HasStage): boolean => stageProfileOf(project) === 'HALTED';
export const isProjectOpportunity = (project: HasStage): boolean => stageProfileOf(project) === 'OPPORTUNITY';
export const isProjectPlanning = (project: HasStage): boolean => stageProfileOf(project) === 'PLANNING';
export const isProjectPlanningLike = (project: HasStage): boolean =>
	['PLANNING', 'OPPORTUNITY'].includes(stageProfileOf(project) ?? '');
export const isProjectRunning = (project: HasStage): boolean => stageProfileOf(project) === 'RUNNING';
export const isProjectActive = (project: HasStage): boolean => ['PLANNING', 'RUNNING'].includes(stageProfileOf(project) ?? '');

/** Whether no other stage in the list has this stage's profile, so deleting it warns the admin first. */
export const isLastWithProfile = (stage: StageLike, stages: ReadonlyArray<StageLike>): boolean =>
	!!stage?.profile && !stages.some(other => other?.id !== stage.id && other?.profile === stage.profile);

/**
 * Whether a list of projects gets an Opportunity group: always with opportunity access, and without it whenever one
 * of the projects is in Opportunity, so a company that lost access still sees those projects.
 */
export const showsOpportunityGroup = (hasOpportunityAccess: boolean, projects: ReadonlyArray<HasStage>): boolean =>
	hasOpportunityAccess || projects.some(isProjectOpportunity);

/** The stage's label: the translated default-stage name when a translation exists, otherwise the name as stored. */
export const stageLabel = (
	stage: StageLike,
	intl: {formatMessage: (d: {id: string}) => string; messages?: Record<string, unknown>}
): string => {
	const name = stage?.name ?? '';
	if (!name) {
		return '';
	}
	const id = `project_status.${name.toLowerCase()}`;
	return intl.messages && Object.prototype.hasOwnProperty.call(intl.messages, id) ? intl.formatMessage({id}) : name;
};

/** A profile section's title: the name of the stage it holds (the first with the profile), else the profile. */
export const profileSectionLabel = (profile: StageProfile, intl: Parameters<typeof stageLabel>[1]): string => {
	const first = stageWithProfile(profile);
	return first ? stageLabel(first, intl) : intl.formatMessage({id: `project_status.${profile.toLowerCase()}`});
};

type CustomStageGroup = {key: string; stage: NonNullable<StageLike>};

/**
 * The stages keyed by their own id (see stageGroupKeyOf) that the given projects are in, one entry per stage, ordered
 * by the cached sortOrder with stages missing from the cache last. Only stages with at least one project appear.
 */
export const customStageGroupsOf = (
	projects: ReadonlyArray<HasStage>,
	cachedStages: ReadonlyArray<{id: string; sortOrder: number}>
): CustomStageGroup[] => {
	const groups = new Map<string, CustomStageGroup>();
	projects.forEach(project => {
		const key = stageGroupKeyOf(project);
		if (key && isCustomStageKey(key) && !groups.has(key) && project?.stage) {
			groups.set(key, {key, stage: project.stage});
		}
	});
	const sortOrders = new Map(cachedStages.map(stage => [stage.id, stage.sortOrder]));
	const orderOf = (key: string): number => sortOrders.get(key) ?? Infinity;
	return [...groups.values()].sort((a, b) => {
		const [aOrder, bOrder] = [orderOf(a.key), orderOf(b.key)];
		return aOrder === bOrder ? 0 : aOrder < bOrder ? -1 : 1;
	});
};
